import { createRequestId } from '@shared/api/request-id'
import { toAppError } from '@shared/api/error'
import { getMemberToken, useMemberSession } from '../model/member-session-store'
import {
  MemberBrandSchema,
  MemberCatalogOfferSchema,
  MemberCourseSchema,
  MemberFilesSchema,
  MemberLessonSchema,
  MemberLibrarySchema,
  MemberMeSchema,
  MemberSessionSchema,
  MemberThankYouSchema,
} from './member-access.contract'
import type {
  MemberBrand,
  MemberCatalogOffer,
  MemberCourse,
  MemberFiles,
  MemberLesson,
  MemberLibrary,
  MemberMe,
  MemberSession,
} from './member-access.contract'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

export function memberApiAuthHeaders(url?: string): Record<string, string> | undefined {
  const token = getMemberToken()
  if (!token) return undefined
  if (url !== undefined) {
    const base = new URL(BASE_URL, window.location.origin).href.replace(/\/$/, '')
    if (!new URL(url, window.location.origin).href.startsWith(`${base}/`)) return undefined
  }
  return { Authorization: `Bearer ${token}` }
}

async function memberFetch<T>(
  path: string,
  options: Omit<RequestInit, 'body'> & { body?: unknown } = {},
): Promise<T> {
  const headers = new Headers(options.headers)
  headers.set('x-request-id', createRequestId())
  const token = getMemberToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  let payload: BodyInit | undefined
  if (options.body !== undefined && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
    payload = JSON.stringify(options.body)
  }
  const response = await fetch(`${BASE_URL}${path}`, { ...options, headers, body: payload })
  const data: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const error = toAppError(response.status, data)
    if (error.isUnauthorized) useMemberSession.getState().clear()
    throw error
  }
  return data as T
}

export async function requestMemberLogin(producerId: string, email: string): Promise<void> {
  await memberFetch('/members-access/login/request', {
    method: 'POST',
    body: { producerId, email },
  })
}

export async function verifyMemberCode(producerId: string, email: string, code: string): Promise<MemberSession> {
  return MemberSessionSchema.parse(
    await memberFetch('/members-access/login/code', {
      method: 'POST',
      body: { producerId, email, code },
    }),
  )
}

export async function consumeMemberMagic(producerId: string, token: string): Promise<MemberSession> {
  return MemberSessionSchema.parse(
    await memberFetch(`/members-access/login/magic?producerId=${encodeURIComponent(producerId)}&token=${encodeURIComponent(token)}`),
  )
}

export async function loginMemberPassword(producerId: string, email: string, password: string): Promise<MemberSession> {
  return MemberSessionSchema.parse(
    await memberFetch('/members-access/login/password', {
      method: 'POST',
      body: { producerId, email, password },
    }),
  )
}

export async function fetchMemberBrand(producerId: string): Promise<MemberBrand> {
  return MemberBrandSchema.parse(
    await memberFetch(`/members-access/login/brand?producerId=${encodeURIComponent(producerId)}`),
  )
}

export async function fetchMemberMe(): Promise<MemberMe> {
  return MemberMeSchema.parse(await memberFetch('/members-access/me'))
}

export async function patchMemberMe(name: string): Promise<MemberMe> {
  return MemberMeSchema.parse(await memberFetch('/members-access/me', { method: 'PATCH', body: { name } }))
}

export async function changeMemberPassword(currentPassword: string, password: string): Promise<void> {
  await memberFetch('/members-access/password', { method: 'POST', body: { currentPassword, password } })
}

export async function uploadMemberAvatar(file: File): Promise<MemberMe> {
  const contentType = file.type === 'image/png' ? 'image/png' : 'image/jpeg'
  const upload = await memberFetch<{ uploadUrl: string; objectKey: string; contentType: string }>(
    '/members-access/me/avatar',
    {
      method: 'POST',
      body: { fileName: file.name, contentType, sizeBytes: file.size },
    },
  )
  if (!upload.uploadUrl.startsWith('memory://') && !upload.uploadUrl.startsWith('local://')) {
    const response = await fetch(upload.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': upload.contentType },
      body: file,
    })
    if (!response.ok) {
      throw new Error('Não foi possível enviar a foto.')
    }
  }
  return MemberMeSchema.parse(
    await memberFetch('/members-access/me/avatar/confirm', { method: 'POST', body: { objectKey: upload.objectKey } }),
  )
}

export async function fetchMemberLibrary(): Promise<MemberLibrary> {
  return MemberLibrarySchema.parse(await memberFetch('/members-access/library'))
}

export async function fetchMemberCatalogOffer(productId: string): Promise<MemberCatalogOffer> {
  return MemberCatalogOfferSchema.parse(await memberFetch(`/members-access/catalog/${productId}`))
}

export async function fetchMemberProduct(productId: string): Promise<
  { readonly kind: 'course'; readonly course: MemberCourse } | { readonly kind: 'files'; readonly files: MemberFiles }
> {
  const data = await memberFetch<unknown>(`/members-access/products/${productId}`)
  if (data && typeof data === 'object' && 'modules' in data) {
    return { kind: 'course', course: MemberCourseSchema.parse(data) }
  }
  return { kind: 'files', files: MemberFilesSchema.parse(data) }
}

export async function fetchMemberLesson(productId: string, lessonId: string): Promise<MemberLesson> {
  return MemberLessonSchema.parse(await memberFetch(`/members-access/products/${productId}/lessons/${lessonId}`))
}

export async function recordMemberProgress(
  productId: string,
  lessonId: string,
  positionSeconds: number,
  durationSeconds: number,
): Promise<void> {
  await memberFetch(`/members-access/products/${productId}/lessons/${lessonId}/progress`, {
    method: 'POST',
    body: { positionSeconds, durationSeconds },
  })
}

export async function completeMemberLesson(productId: string, lessonId: string): Promise<void> {
  await memberFetch(`/members-access/products/${productId}/lessons/${lessonId}/complete`, { method: 'POST' })
}

export async function fetchThankYouAccess(saleId: string, email: string) {
  return MemberThankYouSchema.parse(
    await memberFetch('/members-access/thank-you', { method: 'POST', body: { saleId, email } }),
  )
}
